import _unescape from 'lodash/unescape';
import _memoize from 'lodash/memoize';

function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

import React from 'react';
import { IntlProvider, addLocaleData } from 'react-intl';
import en from 'react-intl/locale-data/en';
import zh from 'react-intl/locale-data/zh';
import de from 'react-intl/locale-data/de';

import moment from 'moment';
import { mapLocale, required, addModuleIfNotExist, hasString, addIdSubModule, getNormalizedHostname } from './utils';
import { DEFAULT_LOCALE, DEFAULT_MODULE_NAME } from './constants';

var NORMALIZED_HOSTNAME = getNormalizedHostname();
var Module = DEFAULT_MODULE_NAME;
var Intl = void 0;
var Locale = DEFAULT_LOCALE;
var Messages = {};
/** In the near future this format will follow the localization,
 * so according to the user country the date format will be different */
var DateFormat = void 0;

/**
 * Initialize the React intl using locale and messages
 * @param module The module name
 * @param locale the user locale
 * @param messages the strings values
 */
export var initialize = function initialize() {
  var locale = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : Locale;
  var messages = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Messages;
  var module = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : Module;
  var dateFormat = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : DateFormat;

  // init local variables
  Module = module;
  Locale = locale;
  Messages = messages;
  DateFormat = dateFormat;

  addLocaleData([].concat(_toConsumableArray(en), _toConsumableArray(zh), _toConsumableArray(de)));
  var intlProvider = new IntlProvider({ locale: mapLocale(locale), messages: messages });
  Intl = intlProvider.getChildContext().intl;
};

/**
 * Get the string value that match id
 * @param id The string id
 * @param defaultMessage The string default value
 * @param values Map of values replace in the string
 * @returns String
 */
export var getStringValue = function getStringValue(id, defaultMessage) {
  var values = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  var idWithModule = addModuleIfNotExist(id, Module);
  var siteStringId = addIdSubModule(idWithModule, NORMALIZED_HOSTNAME);
  // Use the site string if exist
  var newId = hasString(Messages, siteStringId) ? siteStringId : idWithModule;

  // Return the string value component
  return Intl.formatMessage({ id: newId, defaultMessage: defaultMessage }, values);
};

/**
 * The memorized version of getStringValue function
 * @type {Function}
 * @returns Function
 */
var memoizedTranslation = _memoize(getStringValue);

/**
 * The main translation method
 * @param id The string id
 * @param defaultMessage The string default value
 * @param values Map of values replace in the string
 * @returns String
 */
export var t = function t(id) {
  var defaultMessage = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : required();
  var values = arguments[2];
  return !values ? memoizedTranslation(id, defaultMessage, values) : getStringValue(id, defaultMessage, values);
};

/**
 * Format a message that contains any html
 * @param id The string id
 * @param defaultMessage The string default value
 * @param values Map of values replace in the string
 * @returns XML
 */
export function formatHTMLMessage(id) {
  var defaultMessage = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  var values = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  try {
    var siteStringId = addIdSubModule(id, NORMALIZED_HOSTNAME);
    // Use the site string if exist
    var newId = hasString(Messages, siteStringId) ? siteStringId : id;
    // Gets a translated string with escaped html characters. To protect from XSS.
    var translatedHtmlMessage = Intl.formatHTMLMessage({ id: newId, defaultMessage: defaultMessage }, values);

    // Un escape result translated message
    var translatedHTMLComponent = _unescape(translatedHtmlMessage);
    // Render the resultant component inside a span
    return React.createElement('span', { dangerouslySetInnerHTML: { __html: translatedHTMLComponent } });
  } catch (e) {
    console.error('[Translations] error while getting usage of stringID: ' + id, e);
  }
  return undefined;
}

/**
 * This method formats plural messages.
 * It takes the id, value, values, single and plural messages,
 * and return the proper message based on the value.
 * @param id The string id
 * @param value The variable value
 * @param single The single message
 * @param plural The Plural message
 */
export function formatPlurals(id, value, single, plural) {
  return t(id, '{value, plural,one { ' + single + '} other { ' + plural + '}}', { value: value });
}

/**
 * Return the date in a prefered format
 * @param {*} date
 * @param {*} forcedFormat
 */
export function formatDateWithTemplate(date) {
  var forcedFormat = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

  var preferedFormat = DateFormat;
  if (forcedFormat) {
    preferedFormat = forcedFormat;
  }
  try {
    if (date) {
      if (preferedFormat) {
        return moment(date).format(preferedFormat);
      }
      return moment(date).format('ll');
    }
  } catch (error) {
    console.log('Error while parsing the date', date);
  }

  return undefined;
}